import type {
  SceneCamera,
  CameraUUID,
  Quaternion,
  Vector3,
  BodyUUID,
  SiteUUID,
} from "../../types/scene";
import { create } from "mutative";
import { generateUuid } from "../../utils/id";
import type { SceneStoreSetState, SceneStoreGetState } from "../sceneStore.types";
import type { CameraActions, SceneCameraInput } from "./actionTypes";
import { withHistory } from "./withHistory";
import { shouldBlockSceneAuthoringMutation } from "../../utils/physicsGuards";
import { namesFromRecord, nextUniqueName } from "../../utils/uniqueNames";
import { logger } from "../../utils/logger";

const clamp = (v: number, lo: number, hi: number): number => Math.max(lo, Math.min(hi, v));
const DEFAULT_CAMERA_WIDTH = 800;
const DEFAULT_CAMERA_HEIGHT = 800;

const toVec3 = (v: unknown, fallback: Vector3 = [0, 0, 0]): Vector3 => {
  if (!Array.isArray(v) || v.length < 3) return [...fallback];
  const x = Number(v[0]);
  const y = Number(v[1]);
  const z = Number(v[2]);
  return [
    Number.isFinite(x) ? x : fallback[0],
    Number.isFinite(y) ? y : fallback[1],
    Number.isFinite(z) ? z : fallback[2],
  ];
};

const toQuat = (q: unknown, fallback: Quaternion = [1, 0, 0, 0]): Quaternion => {
  if (!Array.isArray(q) || q.length < 4) return [...fallback];
  const w = Number(q[0]);
  const x = Number(q[1]);
  const y = Number(q[2]);
  const z = Number(q[3]);
  if (![w, x, y, z].every(Number.isFinite)) return [...fallback];
  const len = Math.hypot(w, x, y, z);
  if (len < 1e-8) return [...fallback];
  return [w / len, x / len, y / len, z / len];
};

const asBrandedUuid = <T extends string>(value: unknown): T | undefined => {
  if (typeof value !== "string") return undefined;
  const trimmed = value.trim();
  return trimmed ? (trimmed as T) : undefined;
};

const normalizeCamera = (camera: SceneCameraInput): SceneCamera => {
  const uuid = (camera.uuid || generateUuid()) as CameraUUID;
  const rawKind = String(camera.kind || "fixed");
  const kind = rawKind === "mounted" ? "mounted" : "fixed";
  const mountTypeRaw = String(camera.mountType || "body");
  const mountType = mountTypeRaw === "site" ? "site" : "body";
  return {
    uuid,
    name: String(camera.name || `camera_${uuid}`),
    kind,
    position: toVec3(camera.position, [0, 0.5, 2]),
    orientation: toQuat(camera.orientation, [1, 0, 0, 0]),
    fovy: clamp(Number(camera.fovy) || 65, 10, 150),
    width: Math.max(32, Math.floor(Number(camera.width) || DEFAULT_CAMERA_WIDTH)),
    height: Math.max(32, Math.floor(Number(camera.height) || DEFAULT_CAMERA_HEIGHT)),
    fps: Math.max(1, Math.min(60, Math.floor(Number(camera.fps) || 8))),
    mountType: kind === "mounted" ? mountType : undefined,
    mountBodyUuid:
      kind === "mounted" && mountType === "body" && camera.mountBodyUuid
        ? asBrandedUuid<BodyUUID>(camera.mountBodyUuid)
        : undefined,
    mountSiteUuid:
      kind === "mounted" && mountType === "site" && camera.mountSiteUuid
        ? asBrandedUuid<SiteUUID>(camera.mountSiteUuid)
        : undefined,
  };
};

// Validate that a mounted camera's target still exists in the scene. If the
// declared body/site is missing, fall back to a fixed-world camera rather
// than persisting a dangling reference that would break MJCF export.
const resolveCameraMount = (
  camera: SceneCamera,
  bodies: Record<string, unknown> | undefined,
  sites: Record<string, unknown> | undefined
): SceneCamera => {
  if (camera.kind !== "mounted") return camera;
  if (camera.mountType === "site") {
    const siteUuid = camera.mountSiteUuid;
    if (!siteUuid || !sites?.[siteUuid]) {
      logger.warn(
        `[cameraActions] Camera "${camera.name}" targets missing site "${siteUuid ?? "<unset>"}"; downgrading to fixed-world camera.`
      );
      return {
        ...camera,
        kind: "fixed",
        mountType: undefined,
        mountBodyUuid: undefined,
        mountSiteUuid: undefined,
      };
    }
  } else if (camera.mountType === "body") {
    const bodyUuid = camera.mountBodyUuid;
    if (!bodyUuid || !bodies?.[bodyUuid]) {
      logger.warn(
        `[cameraActions] Camera "${camera.name}" targets missing body "${bodyUuid ?? "<unset>"}"; downgrading to fixed-world camera.`
      );
      return {
        ...camera,
        kind: "fixed",
        mountType: undefined,
        mountBodyUuid: undefined,
        mountSiteUuid: undefined,
      };
    }
  }
  return camera;
};

export const createCameraActions = (
  set: SceneStoreSetState,
  get: SceneStoreGetState
): CameraActions => ({
  setCameras: (camerasInput: SceneCameraInput[] | Record<string, SceneCameraInput>) => {
    if (shouldBlockSceneAuthoringMutation("setCameras")) return;
    const arr: SceneCameraInput[] = Array.isArray(camerasInput)
      ? camerasInput
      : Object.values(camerasInput || {});
    const state = get();
    const bodies = state?.bodies;
    const sites = state?.sites;
    const next: Record<string, SceneCamera> = {};
    arr.forEach((cam) => {
      if (!cam) return;
      const normalized = resolveCameraMount(normalizeCamera(cam), bodies, sites);
      next[normalized.uuid] = normalized;
    });
    set((state) =>
      create(state, (draft) => {
        draft.cameras = next;
      })
    );
  },

  addCamera: withHistory((cameraInput: SceneCameraInput): CameraUUID => {
    const state = get();
    const normalizedRaw = resolveCameraMount(
      normalizeCamera(cameraInput),
      state?.bodies,
      state?.sites
    );
    const normalized = {
      ...normalizedRaw,
      name: nextUniqueName(normalizedRaw.name, namesFromRecord(state?.cameras)),
    };
    set((state) =>
      create(state, (draft) => {
        draft.cameras = draft.cameras || {};
        draft.cameras[normalized.uuid] = normalized;
      })
    );
    return normalized.uuid;
  }),

  updateCamera: withHistory((cameraUuid: CameraUUID, updates: Partial<SceneCameraInput>) => {
    const state = get();
    const bodies = state?.bodies;
    const sites = state?.sites;
    set((state) =>
      create(state, (draft) => {
        const prev = draft.cameras?.[cameraUuid];
        if (!prev) return;
        draft.cameras[cameraUuid] = resolveCameraMount(
          normalizeCamera({ ...prev, ...updates, uuid: cameraUuid }),
          bodies,
          sites
        );
      })
    );
  }),

  removeCamera: withHistory((cameraUuid: CameraUUID) => {
    set((state) =>
      create(state, (draft) => {
        if (!draft.cameras) return;
        delete draft.cameras[cameraUuid];
      })
    );
  }),
});
